import React from 'react';
import PlaybookSection from '../../../components/playbooks/PlaybookSection';
import PlaybookCard from '../../../components/playbooks/PlaybookCard';
import ExpandableCallout from '../../../components/playbooks/ExpandableCallout';
import ToolCallout from '../../../components/playbooks/ToolCallout';
import { 
  ChatBubbleLeftRightIcon,
  DocumentTextIcon,
  MegaphoneIcon,
  CheckCircleIcon,
  LightBulbIcon,
  AdjustmentsHorizontalIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

const ResettingBehavioursStage1 = () => {
  return (
    <div className="space-y-8">
      <PlaybookSection
        title="Stage 1: Address the elephant in the room"
        icon={<ChatBubbleLeftRightIcon className="h-7 w-7 text-primary-light" />}
        heading="Gather examples and publicly address the problem"
      >
        <ExpandableCallout
          type="info"
          title="What do I need to DO in this stage?"
          defaultOpen={true}
        >
          <div className="space-y-4">
            <p className="text-gray-300">You need to do 2 things:</p>
            <ol className="space-y-4">
              <li className="flex items-start">
                <span className="text-primary-light font-bold mr-2">1.</span>
                <span className="text-gray-300">Gather tangible examples of the observed behaviour</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary-light font-bold mr-2">2.</span>
                <span className="text-gray-300">Publicly address the problem and how it is impacting the business</span>
              </li>
            </ol>
          </div>
        </ExpandableCallout>

        <div className="p-4 bg-gray-800/30 rounded-lg border border-gray-700/50 mb-8">
          <div className="flex items-start">
            <LightBulbIcon className="h-6 w-6 text-primary-light mr-3 mt-1" />
            <div>
              <p className="text-gray-300">
                I've worked in some organisations where this step alone solves the problem. Depending on your leadership team this can either be you or founder/ceo delivering the message. I won't beat around the bush – it takes courage, excellent non-violent communication skills and high emotional intelligence.
              </p>
              <p className="text-gray-300 mt-2">
                Those 3 things should be a filter you apply to decide who is best to deliver this message.
              </p>
            </div>
          </div>
        </div>

        <PlaybookCard>
          <h2 className="text-xl font-semibold text-white mb-4 flex items-center">
            <DocumentTextIcon className="h-6 w-6 text-primary-light mr-3" />
            Step 1: Gather examples and their perceived impact
          </h2>
          <div className="space-y-6">
            <div className="border-l-4 border-primary-light pl-4 italic text-gray-300">
              <p>Data talks. Examples talk.</p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-white mb-2">Collect:</h3>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <span className="text-primary-light mr-2">—</span>
                  <span className="text-gray-300">Your observations</span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary-light mr-2">—</span>
                  <span className="text-gray-300">The observations of others</span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary-light mr-2">—</span>
                  <span className="text-gray-300">Any thematic insights from team feedback, 121 feedback, surveys, pulses</span>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-white mb-2">Example Scenarios:</h3>
              <div className="space-y-6">
                <div className="p-4 bg-gray-800/30 rounded-lg border border-gray-700/50">
                  <div className="flex items-center mb-2">
                    <ChatBubbleLeftRightIcon className="h-6 w-6 text-primary-light mr-2" />
                    <h4 className="text-white font-semibold">Communication Breakdown</h4>
                  </div>
                  <p className="text-gray-300 mb-2">During our recent product launch, several team members expressed frustration over the lack of clear communication channels. This miscommunication led to duplicated efforts and missed deadlines, which contradicts our value of transparency.</p>
                  <p className="text-gray-300"><strong>Impact:</strong> Reduced efficiency, lowered team morale, and increased project costs due to rework.</p>
                </div>

                <div className="p-4 bg-gray-800/30 rounded-lg border border-gray-700/50">
                  <div className="flex items-center mb-2">
                    <AdjustmentsHorizontalIcon className="h-6 w-6 text-primary-light mr-2" />
                    <h4 className="text-white font-semibold">Decision Making</h4>
                  </div>
                  <p className="text-gray-300 mb-2">In our feedback surveys, multiple team members mentioned that decisions are often made by a small group without broader team input. This creates a sense of exclusion and undermines our value of inclusivity.</p>
                  <p className="text-gray-300"><strong>Impact:</strong> A lack of diverse perspectives in decision-making and decisions being made far away from the team that does the work.</p>
                </div>

                <div className="p-4 bg-gray-800/30 rounded-lg border border-gray-700/50">
                  <div className="flex items-center mb-2">
                    <ExclamationTriangleIcon className="h-6 w-6 text-primary-light mr-2" />
                    <h4 className="text-white font-semibold">Blame Culture</h4>
                  </div>
                  <p className="text-gray-300 mb-2">I've observed that when feature delivery don't go as planned, there is a tendency for teams to blame each other rather than working together to find solutions. For example, during the last product retro, when a critical feature wasn't delivered on time, we spent more time focusing on individual shortcomings than on how to avoid the issues happening again.</p>
                  <p className="text-gray-300"><strong>Impact:</strong> This behavior creates a toxic work environment, reduces trust, and hampers collaboration. It goes against our core values of teamwork and accountability, and isn't how we win.</p>
                </div>
              </div>
            </div>
          </div>
        </PlaybookCard>

        <PlaybookCard>
          <h2 className="text-xl font-semibold text-white mb-4 flex items-center">
            <MegaphoneIcon className="h-6 w-6 text-primary-light mr-3" />
            Step 2: Publicly address the problem
          </h2>
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-white">Town Hall Meeting</h3>
            
            <div>
              <h4 className="text-white font-semibold mb-2">Preparation:</h4>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <span className="text-primary-light mr-2">—</span>
                  <span className="text-gray-300">Schedule a slot in your company-wide town hall meeting</span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary-light mr-2">—</span>
                  <span className="text-gray-300">Prepare a presentation that outlines your core values and behaviours, how they help everyone succeed, observed anti-behaviours, and the impact of these behaviours on the business</span>
                </li>
              </ul>
            </div>

            <div className="border-l-4 border-primary-light pl-4 italic text-gray-300">
              <p>"I've recently noticed some behaviours that don't align with our core values and how we do our best work together. This is impacting our trust, performance, and overall success. It's important we address this head-on, so I'm going to spend some time today refreshing the expectations of how we work together."</p>
            </div>

            <div>
              <h4 className="text-white font-semibold mb-2">Presentation Structure:</h4>
              <ol className="space-y-4">
                <li className="flex items-start">
                  <span className="text-primary-light font-bold mr-2">1.</span>
                  <div>
                    <span className="text-white font-semibold">Core Values Overview</span>
                    <ul className="mt-2 space-y-2">
                      <li className="flex items-start">
                        <span className="text-primary-light mr-2">—</span>
                        <span className="text-gray-300">Refresh on 'how we do our best work'</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-primary-light mr-2">—</span>
                        <span className="text-gray-300">Explain why they are important</span>
                      </li>
                    </ul>
                  </div>
                </li>
                <li className="flex items-start">
                  <span className="text-primary-light font-bold mr-2">2.</span>
                  <div>
                    <span className="text-white font-semibold">Examples of Anti-Behaviours</span>
                    <p className="text-gray-300 mt-2">Share specific examples (without naming and shaming) to illustrate the gap between current and target.</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <span className="text-primary-light font-bold mr-2">3.</span>
                  <div>
                    <span className="text-white font-semibold">Business Impact</span>
                    <p className="text-gray-300 mt-2">Link to the goals you need to achieve together.</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <span className="text-primary-light font-bold mr-2">4.</span>
                  <div>
                    <span className="text-white font-semibold">Path Forward</span>
                    <ul className="mt-2 space-y-2">
                      <li className="flex items-start">
                        <span className="text-primary-light mr-2">—</span>
                        <span className="text-gray-300">Invite action: "if you see this, Call it out."</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-primary-light mr-2">—</span>
                        <span className="text-gray-300">Talk to me. I appreciate that with some of this, there's often something going on underneath.</span>
                      </li>
                    </ul>
                  </div>
                </li>
              </ol>
            </div>
          </div>
        </PlaybookCard>

        <PlaybookCard>
          <h2 className="text-xl font-semibold text-white mb-4 flex items-center">
            <CheckCircleIcon className="h-6 w-6 text-primary-light mr-3" />
            Outcome
          </h2>
          <ul className="space-y-4">
            <li className="flex items-start">
              <span className="text-primary-light mr-2">—</span>
              <span className="text-gray-300">You transparently met the problem head on vs letting it fester</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light mr-2">—</span>
              <span className="text-gray-300">You invited others to acknowledge the problem and call it out</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light mr-2">—</span>
              <span className="text-gray-300">You (re)set expectations</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light mr-2">—</span>
              <span className="text-gray-300">You took ownership</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light mr-2">—</span>
              <span className="text-gray-300">You gave space for people to feel heard</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light mr-2">—</span>
              <span className="text-gray-300">You made your door open to feedback for any deeper issue</span>
            </li>
          </ul>
        </PlaybookCard>

        <div className="p-4 bg-gray-800/30 rounded-lg border border-gray-700/50">
          <div className="flex items-start">
            <LightBulbIcon className="h-6 w-6 text-primary-light mr-3" />
            <div>
              <p className="text-gray-300">
                Sometimes this step alone is enough to course correct. It depends on how pressured and stressed the teams are and whether the anti-behaviours you are seeing are rooted in something much deeper or systemic. If in the next step you find they are, then there's a good chance you might need to leverage one of the toolkits in Working Through Conflict: 3 Scenario Based Toolkits.
              </p>
            </div>
          </div>
        </div>

        <ToolCallout
          tool="default"
          label="Non-violent Communication Guide"
          href="https://www.dave-bailey.com/blog/nonviolent-communication"
          description="A framework for effective communication during difficult conversations"
        />
      </PlaybookSection>
    </div>
  );
};

export default ResettingBehavioursStage1; 